package com.trevorwiebe.timesheet.admin.organization.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.model.Organization
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime

class OrganizationViewModel(
    private val coreRepository: CoreRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(OrganizationState())
    val state = _state.asStateFlow()

    // The org id and the original time-of-day of the go-live date are not edited in the UI,
    // but we keep them so saving writes back the same document and preserves the stored
    // instant's time component (only the date is editable, matching the old admin panel).
    private var orgId: String? = null
    private var goLiveTime: LocalTime = LocalTime(0, 0)

    init {
        load()
    }

    private fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val result = coreRepository.getOrganization()
            if (result.error != null) {
                _state.update { it.copy(isLoading = false, error = result.error) }
            } else {
                val organization = result.data as Organization
                orgId = organization.id
                goLiveTime = organization.goLiveDate.time
                _state.update {
                    it.copy(
                        isLoading = false,
                        name = organization.name,
                        goLiveDate = organization.goLiveDate.date,
                        billingAddress = organization.billingAddress,
                        billingPhone = organization.billingPhone,
                        billingEmail = organization.billingEmail,
                        remitTo = organization.remitTo,
                        error = null,
                    )
                }
            }
        }
    }

    fun onEvent(event: OrganizationEvent) {
        when (event) {
            is OrganizationEvent.OnNameChanged ->
                _state.update { it.copy(name = event.name) }

            is OrganizationEvent.OnGoLiveDateChanged ->
                _state.update { it.copy(goLiveDate = event.date) }

            is OrganizationEvent.OnBillingAddressChanged -> _state.update { it.copy(billingAddress = event.text) }
            is OrganizationEvent.OnBillingPhoneChanged -> _state.update { it.copy(billingPhone = event.text) }
            is OrganizationEvent.OnBillingEmailChanged -> _state.update { it.copy(billingEmail = event.text) }
            is OrganizationEvent.OnRemitToChanged -> _state.update { it.copy(remitTo = event.text) }

            is OrganizationEvent.OnSave -> save()

            is OrganizationEvent.OnErrorShown ->
                _state.update { it.copy(error = null) }

            is OrganizationEvent.OnSavedHandled ->
                _state.update { it.copy(isSaved = false) }
        }
    }

    private fun save() {
        val id = orgId ?: return
        viewModelScope.launch {
            _state.update { it.copy(isSaving = true) }
            val current = _state.value
            val organization = Organization(
                id = id,
                name = current.name.trim(),
                goLiveDate = LocalDateTime(current.goLiveDate, goLiveTime),
                billingAddress = current.billingAddress.trim(),
                billingPhone = current.billingPhone.trim(),
                billingEmail = current.billingEmail.trim(),
                remitTo = current.remitTo.trim(),
            )
            val result = coreRepository.updateOrganization(organization)
            if (result.error != null) {
                _state.update { it.copy(isSaving = false, error = result.error) }
            } else {
                _state.update { it.copy(isSaving = false, isSaved = true) }
            }
        }
    }
}
